import type { NotifiableError } from "@bugsnag/js";
import { CombinedError } from "urql";
import {
  graphQLErrorMetadataFromCombinedError,
  mergeGraphQLErrorMetadataForBugsnag,
  mergeGraphQLTraceMetadataForBugsnag,
  traceMetadataFromCombinedError,
} from "@/graphql/trace-context-exchange";
import { Bugsnag, bugsnagStarted } from "./bugsnag-init";
import {
  isStaleDeploymentError,
  reportStaleDeployment,
} from "./stale-deployment";

function notifyError(
  error: NotifiableError,
  metadata?: Record<string, unknown>,
) {
  if (!bugsnagStarted) return;

  Bugsnag.notify(error, (event) => {
    if (metadata && Object.keys(metadata).length > 0) {
      const {
        "Trace Context": traceContext,
        GraphQL,
        "AI Tool": aiTool,
        ...customLogMetadata
      } = metadata;
      let graphQLOperationName: string | undefined;
      if (traceContext && typeof traceContext === "object") {
        event.addMetadata(
          "Trace Context",
          traceContext as Record<string, unknown>,
        );
      }
      if (GraphQL && typeof GraphQL === "object") {
        const graphQLMetadata = GraphQL as Record<string, unknown>;
        event.addMetadata("GraphQL", graphQLMetadata);
        if (typeof graphQLMetadata.operation_name === "string") {
          graphQLOperationName = graphQLMetadata.operation_name;
        }
      }
      if (aiTool && typeof aiTool === "object") {
        const aiToolMetadata = aiTool as Record<string, unknown>;
        const { user_id: userId, ...toolMetadata } = aiToolMetadata;
        if (typeof userId === "string" && userId.length > 0) {
          event.setUser(userId);
        }
        event.addMetadata("AI Tool", toolMetadata);
      }
      if (event.context === undefined) {
        if (graphQLOperationName) {
          event.context = `GraphQL operation: ${graphQLOperationName}`;
        } else if (aiTool && typeof aiTool === "object") {
          event.context = "AI tool execution";
        }
      }
      if (Object.keys(customLogMetadata).length > 0) {
        event.addMetadata("custom_log", customLogMetadata);
      }
    }
  });
}

/**
 * 観測境界で付与した AI tool の文脈を保ったまま、標準 `cause` chain から
 * 元の urql error を見つけ、response 単位の trace context を回収します。
 */
function combinedErrorFromCauseChain(error: Error): CombinedError | undefined {
  const seen = new Set<Error>();
  let current: Error | undefined = error;
  while (current && !seen.has(current)) {
    if (current instanceof CombinedError) return current;
    seen.add(current);
    current = current.cause instanceof Error ? current.cause : undefined;
  }
  return undefined;
}

const MAX_SERIALIZED_ERROR_DEPTH = 5;
const MAX_SERIALIZED_AGGREGATE_ERRORS = 10;
const MAX_SERIALIZED_ERROR_NODES = 20;
const MAX_SERIALIZED_ERROR_TEXT_LENGTH = 64_000;

type SerializedErrorReference =
  | SerializedError
  | { type: "non-error"; value: string }
  | {
      type: "truncated";
      reason: "circular" | "max-depth" | "max-items" | "max-total-errors";
    };

type SerializedError = {
  name: string;
  message: string;
  stack?: string;
  code?: string | number;
  cause?: SerializedErrorReference;
  errors?: SerializedErrorReference[];
};

type ErrorSerializationState = {
  activeErrors: WeakSet<object>;
  remainingErrorNodes: number;
  remainingTextLength: number;
};

function truncateErrorText(
  value: string,
  state: ErrorSerializationState,
): string {
  if (value.length <= state.remainingTextLength) {
    state.remainingTextLength -= value.length;
    return value;
  }

  const suffix = "...[truncated]";
  const availableContentLength = Math.max(
    0,
    state.remainingTextLength - suffix.length,
  );
  state.remainingTextLength = 0;
  return `${value.slice(0, availableContentLength)}${suffix}`;
}

function readErrorProperty(error: Error, key: PropertyKey): unknown {
  try {
    return Reflect.get(error, key);
  } catch {
    return undefined;
  }
}

function serializeNonErrorCause(
  cause: unknown,
  state: ErrorSerializationState,
): SerializedErrorReference {
  if (cause === null) return { type: "non-error", value: "null" };

  switch (typeof cause) {
    case "string":
      return { type: "non-error", value: truncateErrorText(cause, state) };
    case "number":
    case "boolean":
    case "bigint":
    case "undefined":
      return {
        type: "non-error",
        value: truncateErrorText(String(cause), state),
      };
    case "symbol":
      return {
        type: "non-error",
        value: cause.description
          ? `Symbol(${truncateErrorText(cause.description, state)})`
          : "Symbol()",
      };
    case "function":
      return { type: "non-error", value: "[function]" };
    case "object":
      return { type: "non-error", value: "[object]" };
  }

  return { type: "non-error", value: "[unknown]" };
}

/**
 * Cloud Logging に出してよい診断フィールドだけを Error から抽出します。
 * custom property は response や認証情報を含み得るため、列挙せず固定 schema にします。
 */
function serializeErrorForCloudLogging(
  error: Error,
  state: ErrorSerializationState = {
    activeErrors: new WeakSet<object>(),
    remainingErrorNodes: MAX_SERIALIZED_ERROR_NODES,
    remainingTextLength: MAX_SERIALIZED_ERROR_TEXT_LENGTH,
  },
  depth = 0,
): SerializedErrorReference {
  if (state.activeErrors.has(error)) {
    return { type: "truncated", reason: "circular" };
  }
  if (depth >= MAX_SERIALIZED_ERROR_DEPTH) {
    return { type: "truncated", reason: "max-depth" };
  }
  if (state.remainingErrorNodes <= 0) {
    return { type: "truncated", reason: "max-total-errors" };
  }
  state.remainingErrorNodes -= 1;
  state.activeErrors.add(error);

  const name = readErrorProperty(error, "name");
  const message = readErrorProperty(error, "message");
  const stack = readErrorProperty(error, "stack");
  const code = readErrorProperty(error, "code");
  const serialized: SerializedError = {
    name: typeof name === "string" ? truncateErrorText(name, state) : "Error",
    message:
      typeof message === "string"
        ? truncateErrorText(message, state)
        : "[message unavailable]",
  };

  if (typeof stack === "string") {
    serialized.stack = truncateErrorText(stack, state);
  }
  if (typeof code === "string") {
    serialized.code = truncateErrorText(code, state);
  } else if (typeof code === "number" && Number.isFinite(code)) {
    serialized.code = code;
  }

  const cause = readErrorProperty(error, "cause");
  if (cause !== undefined) {
    serialized.cause =
      cause instanceof Error
        ? serializeErrorForCloudLogging(cause, state, depth + 1)
        : serializeNonErrorCause(cause, state);
  }

  if (error instanceof AggregateError) {
    const aggregateErrors = readErrorProperty(error, "errors");
    if (Array.isArray(aggregateErrors)) {
      serialized.errors = aggregateErrors
        .slice(0, MAX_SERIALIZED_AGGREGATE_ERRORS)
        .map((item) =>
          item instanceof Error
            ? serializeErrorForCloudLogging(item, state, depth + 1)
            : serializeNonErrorCause(item, state),
        );
      if (aggregateErrors.length > MAX_SERIALIZED_AGGREGATE_ERRORS) {
        serialized.errors.push({ type: "truncated", reason: "max-items" });
      }
    }
  }

  state.activeErrors.delete(error);
  return serialized;
}

/**
 * Cloud Loggingで複数行ログが切れないように、
 * 本番環境のサーバーサイドではオブジェクトをJSON文字列化する
 */
function formatForCloudLogging(...args: unknown[]): unknown[] {
  // 本番環境のサーバーサイドのみ特別な処理
  if (process.env.NODE_ENV === "production" && typeof window === "undefined") {
    return args.map((arg) => {
      // オブジェクトや配列はJSON文字列化
      if (arg !== null && typeof arg === "object") {
        try {
          return JSON.stringify(
            arg instanceof Error ? serializeErrorForCloudLogging(arg) : arg,
          );
        } catch {
          return String(arg);
        }
      }
      return arg;
    });
  }
  // 開発環境やクライアントサイドはそのまま
  return args;
}

/** Business identifiers belong to the request's Bugsnag event, not Cloud Logging. */
function withoutToolBusinessIds(value: unknown): unknown {
  if (!value || typeof value !== "object" || !("AI Tool" in value)) {
    return value;
  }
  const metadata = value as Record<string, unknown>;
  const aiTool = metadata["AI Tool"];
  if (!aiTool || typeof aiTool !== "object") {
    return value;
  }
  const {
    project_id: _projectId,
    workspace_id: _workspaceId,
    user_id: _userId,
    ...loggableAiTool
  } = aiTool as Record<string, unknown>;
  return { ...metadata, "AI Tool": loggableAiTool };
}

export const logger = {
  error: (...args: unknown[]): void => {
    const logArgs = args.map(withoutToolBusinessIds);
    // deployment skew は再読み込みで解消するアプリ外の事象なので、
    // Bugsnag の error 通知ではなく再読み込み導線に振り替える
    if (logArgs.some((arg) => isStaleDeploymentError(arg))) {
      console.warn(...formatForCloudLogging(...logArgs));
      if (bugsnagStarted) {
        Bugsnag.leaveBreadcrumb(
          "Stale deployment",
          { arguments: logArgs.map(String) },
          "log",
        );
      }
      reportStaleDeployment();
      return;
    }

    console.error(...formatForCloudLogging(...logArgs));

    let errorToNotify: NotifiableError;
    const foundError = args.find((arg) => arg instanceof Error);

    if (foundError instanceof Error) {
      errorToNotify = foundError;
    } else {
      const message =
        typeof args[0] === "string" ? args[0] : "Unknown console error";
      errorToNotify = new Error(message);
    }

    // メタデータの抽出: Errorインスタンス以外の最初のオブジェクトをメタデータとして扱う
    // それ以外の引数はBugsnagのメタデータとしては無視する (console.errorには渡されている)
    let bugsnagMetadata: Record<string, unknown> | undefined;
    const potentialMetadata = args.find(
      (arg) => arg !== errorToNotify && typeof arg === "object" && arg !== null,
    );

    if (potentialMetadata) {
      bugsnagMetadata = potentialMetadata as Record<string, unknown>;
    }
    // メタデータオブジェクトがない場合、Errorインスタンス以外の引数を"arguments"キーで渡すことも検討できるが、
    // シンプルにするため、ここでは明示的なメタデータオブジェクトのみを対象とする。
    // logger.error(err, "message", { data: 1}) のような呼び出しで "message" をメタデータに含めたい場合は、
    // ここのロジックを調整する必要がある。現状は、最初のオブジェクトのみ。

    const combinedError =
      errorToNotify instanceof Error
        ? combinedErrorFromCauseChain(errorToNotify)
        : undefined;
    if (combinedError) {
      const bugsnagMetadataWithGraphQL = mergeGraphQLErrorMetadataForBugsnag(
        bugsnagMetadata,
        graphQLErrorMetadataFromCombinedError(combinedError),
      );
      const bugsnagMetadataWithTrace = mergeGraphQLTraceMetadataForBugsnag(
        bugsnagMetadataWithGraphQL,
        traceMetadataFromCombinedError(combinedError),
      );
      // 直接渡された CombinedError は従来どおり内訳を通知します。cause chain
      // 内にある場合は、観測境界の文脈を持つ outer error を metadata 付きで通知します。
      if (combinedError === errorToNotify) {
        if (combinedError.networkError) {
          notifyError(combinedError.networkError, bugsnagMetadataWithTrace);
        }
        for (const error of combinedError.graphQLErrors) {
          notifyError(error, bugsnagMetadataWithTrace);
        }
      } else {
        notifyError(errorToNotify, bugsnagMetadataWithTrace);
      }
    } else {
      notifyError(errorToNotify, bugsnagMetadata);
    }
  },
  warn: (...args: unknown[]): void => {
    console.warn(...formatForCloudLogging(...args));
    if (bugsnagStarted) {
      Bugsnag.leaveBreadcrumb(
        "Console warn",
        { arguments: args.map(String) },
        "log",
      );
    }
  },
  info: (...args: unknown[]): void => {
    console.info(...formatForCloudLogging(...args));
    if (bugsnagStarted) {
      Bugsnag.leaveBreadcrumb(
        "Console info",
        { arguments: args.map(String) },
        "log",
      );
    }
  },
  debug: (...args: unknown[]): void => {
    console.debug(...formatForCloudLogging(...args));
    if (bugsnagStarted) {
      Bugsnag.leaveBreadcrumb(
        "Console debug",
        { arguments: args.map(String) },
        "log",
      );
    }
  },
  log: (...args: unknown[]): void => {
    console.log(...formatForCloudLogging(...args));
    if (bugsnagStarted) {
      Bugsnag.leaveBreadcrumb(
        "Console log",
        { arguments: args.map(String) },
        "log",
      );
    }
  },
};
